import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToastContext } from '../../contexts/ToastContext';
import { supabaseAuth } from '../../services/supabaseAuth';
import { User } from '../../services/userAuth';
import { Eye, EyeOff } from 'lucide-react';
import { logger } from '../../utils/logger';
import { getTranslation } from '../../utils/translationHelpers';
import { AuthModal } from './AuthModal';
import './LoginModal.css';

/** Internal marker for password sign-in timeout (not shown to users). */
const SIGN_IN_TIMEOUT = '__tcoach_sign_in_timeout__';

const MAGIC_LINK_TEXTS = {
  en: {
    tabMagic: 'Magic link',
    tabPassword: 'Password',
    sendButton: 'Send magic link',
    sending: 'Sending...',
    success: 'Check your email for the login link. It expires in 10 minutes.',
    error: 'Failed to send magic link. Please try again.',
  },
  nl: {
    tabMagic: 'Magic link',
    tabPassword: 'Wachtwoord',
    sendButton: 'Stuur magic link',
    sending: 'Verzenden...',
    success: 'Controleer je e-mail voor de inloglink. Deze verloopt over 10 minuten.',
    error: 'Magic link versturen mislukt. Probeer het opnieuw.',
  },
  ar: {
    tabMagic: 'رابط الدخول',
    tabPassword: 'كلمة المرور',
    sendButton: 'أرسل رابط الدخول',
    sending: 'جار الإرسال...',
    success: 'تحقق من بريدك للحصول على رابط الدخول. ينتهي خلال 10 دقائق.',
    error: 'فشل إرسال رابط الدخول. يرجى المحاولة مرة أخرى.',
  },
} as const;

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: User) => void;
  onSwitchToSignup: () => void;
  fullPage?: boolean;
  returnTo?: string;
}

export const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onSwitchToSignup,
  fullPage = false,
  returnTo,
}) => {
  const { t_nested, currentLanguage } = useLanguage();
  const toast = useToastContext();
  const [activeTab, setActiveTab] = useState<'magiclink' | 'password'>('magiclink');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lang = (currentLanguage === 'nl' ? 'nl' : currentLanguage === 'ar' ? 'ar' : 'en') as keyof typeof MAGIC_LINK_TEXTS;
  const magicT = MAGIC_LINK_TEXTS[lang];

  const persistAuthReturnTarget = () => {
    if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      localStorage.setItem('authReturnTo', returnTo);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = magicLinkEmail.trim();
    if (!email) return;
    setMagicLinkError(null);
    setMagicLinkLoading(true);
    try {
      persistAuthReturnTarget();
      const result = await supabaseAuth.signInWithMagicLink(email);
      if (result.error) {
        setMagicLinkError(result.error.message || magicT.error);
        setMagicLinkLoading(false);
        return;
      }
      setMagicLinkSent(true);
    } catch (err: unknown) {
      logger.error('Magic link error:', err);
      setMagicLinkError(magicT.error);
    }
    setMagicLinkLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Add timeout to prevent hanging on slow network (15 seconds)
      const signInWithTimeout = async () => {
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(SIGN_IN_TIMEOUT)), 15000)
        );
        const signInPromise = supabaseAuth.signInWithEmail(
          formData.email,
          formData.password
        );
        return Promise.race([signInPromise, timeoutPromise]);
      };
      
      const result = await signInWithTimeout();

      if (result.error) {
        // Handle specific error messages
        let errorMessage = result.error.message || result.error.toString();
        
        // User-friendly error messages
        if (errorMessage.includes('Invalid login credentials') || errorMessage.includes('invalid_credentials')) {
          errorMessage = getTranslation(t_nested, 'auth.invalidCredentials', 'Invalid email or password. Please try again.');
        } else if (errorMessage.includes('Email not confirmed')) {
          errorMessage = getTranslation(t_nested, 'auth.emailNotVerified', 'Please verify your email address before signing in.');
        } else if (errorMessage.includes('Too many requests')) {
          errorMessage = getTranslation(t_nested, 'auth.tooManyAttempts', 'Too many sign-in attempts. Please wait a moment and try again.');
        }

        logger.error('Login error:', result.error);
        setError(
          errorMessage ||
            getTranslation(t_nested, 'auth.authenticationFailedEmailPassword', 'Authentication failed. Please check your email and password.')
        );
        setIsLoading(false);
        return;
      }

      if (result.user) {
        const user: User = {
          id: result.user.id,
          email: result.user.email,
          name: result.user.name,
          createdAt: new Date().toISOString(),
          isGuest: false
        };

        // Batch localStorage writes for better performance (Netflix/Duolingo pattern)
        const userData = {
          userAuthenticated: 'true',
          username: user.name,
          userEmail: user.email,
          userId: user.id,
          currentUser: JSON.stringify(user)
        };
        Object.entries(userData).forEach(([key, value]) => {
          localStorage.setItem(key, value);
        });

        // Call success handler immediately (don't wait for anything)
        // This ensures instant navigation
        onSuccess(user);
        onClose();
      } else {
        logger.error('Login failed: No user returned');
        setError(getTranslation(t_nested, 'auth.authenticationFailed', 'Authentication failed. Please try again.'));
        setIsLoading(false);
      }
    } catch (error: any) {
      logger.error('Login error:', error);
      let errorMessage = error?.message || error?.toString() || '';
      if (errorMessage === SIGN_IN_TIMEOUT) {
        errorMessage = getTranslation(t_nested, 'auth.signInTimedOut', 'Sign-in request timed out. Please check your internet connection and try again.');
      } else if (!errorMessage) {
        errorMessage = getTranslation(t_nested, 'auth.authenticationFailed', 'Authentication failed. Please try again.');
      }
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    try {
      persistAuthReturnTarget();
      // Google OAuth redirects immediately, so we don't wait for a user
      // The redirect will happen and user will be handled in AuthCallback
      const supabaseUser = await supabaseAuth.signInWithGoogle();
      
      if (supabaseUser) {
        // Immediate return (shouldn't happen with OAuth, but handle just in case)
        const user: User = {
          id: supabaseUser.id,
          email: supabaseUser.email,
          name: supabaseUser.name,
          createdAt: new Date().toISOString(),
          isGuest: false
        };
        
        // Batch localStorage writes for better performance
        const userData = {
          userAuthenticated: 'true',
          username: user.name,
          userEmail: user.email,
          currentUser: JSON.stringify(user)
        };
        Object.entries(userData).forEach(([key, value]) => {
          localStorage.setItem(key, value);
        });
        
        onSuccess(user);
        onClose();
        toast.success(getTranslation(t_nested, 'auth.googleSignInSuccess', 'Successfully signed in with Google!'));
      } else {
        // OAuth redirect will happen immediately - don't keep loading state
        // The page will redirect to Google, then back to /auth/callback
        toast.info(getTranslation(t_nested, 'auth.googleSignInRedirecting', 'Redirecting to Google sign-in...'));
        // Don't set loading to false - let the redirect happen
      }
    } catch (error: any) {
      logger.error('Google sign-in error:', error);
      const errorMessage =
        error?.message ||
        getTranslation(t_nested, 'auth.googleSignInFailed', 'Google sign-in failed. Please try again.');
      setError(errorMessage);
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  const content = (
    <div className="login-modal-content">
        <div className="auth-modal-header">
          <h2 className="auth-modal-title">
            {getTranslation(t_nested, 'auth.welcomeBack', 'Welcome back')}
          </h2>
          <p className="auth-modal-subtitle">
            {getTranslation(t_nested, 'auth.loginSubtitle', 'Log in to continue learning')}
          </p>
        </div>

        {/* Google Sign-in First - Most Prominent */}
        <button
          className="auth-modal-button auth-modal-button-google auth-modal-button-google-first"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="auth-loading-spinner"></div>
          ) : (
            <>
              <svg className="auth-google-logo" width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>{getTranslation(t_nested, 'auth.continueWithGoogle', 'Continue with Google')}</span>
            </>
          )}
        </button>

        <div className="auth-modal-divider">
          <span className="auth-modal-divider-text">
            {getTranslation(t_nested, 'auth.or', 'or')}
          </span>
        </div>

        <div className="auth-login-tabs" role="tablist" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'magiclink'}
            className={`auth-login-tab ${activeTab === 'magiclink' ? 'auth-login-tab-active' : ''}`}
            onClick={() => {
              setActiveTab('magiclink');
              setMagicLinkError(null);
            }}
          >
            {magicT.tabMagic}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'password'}
            className={`auth-login-tab ${activeTab === 'password' ? 'auth-login-tab-active' : ''}`}
            onClick={() => {
              setActiveTab('password');
              setError(null);
            }}
          >
            {magicT.tabPassword}
          </button>
        </div>

        {activeTab === 'magiclink' ? (
          <div className="auth-magic-link-content" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
            {magicLinkSent ? (
              <p className="auth-magic-link-success">{magicT.success}</p>
            ) : (
              <form className="auth-modal-form" onSubmit={handleMagicLinkSubmit}>
                <div className="auth-input-group">
                  <input
                    type="email"
                    placeholder={getTranslation(t_nested, 'auth.login.emailPlaceholder', 'Email')}
                    value={magicLinkEmail}
                    onChange={(e) => {
                      setMagicLinkEmail(e.target.value);
                      setMagicLinkError(null);
                    }}
                    required
                    className="auth-modal-input"
                    disabled={magicLinkLoading}
                  />
                </div>
                <button
                  type="submit"
                  className="auth-modal-button auth-modal-button-magic"
                  disabled={magicLinkLoading}
                >
                  {magicLinkLoading ? magicT.sending : magicT.sendButton}
                </button>
                {magicLinkError && (
                  <p className="auth-magic-link-error">{magicLinkError}</p>
                )}
              </form>
            )}
          </div>
        ) : (
          <form className="auth-modal-form" onSubmit={handleSubmit}>
            {error && (
              <div className="auth-error-message">
                {error}
              </div>
            )}

            <div className="auth-input-group">
              <input
                type="email"
                name="email"
                placeholder={getTranslation(t_nested, 'auth.login.emailPlaceholder', 'Email')}
                value={formData.email}
                onChange={handleInputChange}
                required
                className="auth-modal-input"
                disabled={isLoading}
              />
            </div>

            <div className="auth-input-group auth-password-group">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder={getTranslation(t_nested, 'auth.login.passwordPlaceholder', 'Password')}
                value={formData.password}
                onChange={handleInputChange}
                required
                className="auth-modal-input"
                disabled={isLoading}
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? getTranslation(t_nested, 'auth.hidePassword', 'Hide password') : getTranslation(t_nested, 'auth.showPassword', 'Show password')}
                tabIndex={0}
                disabled={isLoading}
              >
                {showPassword ? <EyeOff className="auth-password-icon" /> : <Eye className="auth-password-icon" />}
              </button>
            </div>

            <div className="auth-forgot-password">
              <button
                type="button"
                className="auth-forgot-password-link"
                onClick={() =>
                  toast.info(
                    getTranslation(t_nested, 'auth.forgotPasswordComingSoon', 'Forgot-password recovery is not available yet.')
                  )
                }
                disabled={isLoading}
              >
                {getTranslation(t_nested, 'auth.forgotPassword', 'Forgot password?')}
              </button>
            </div>

            <button
              type="submit"
              className={`auth-modal-button auth-modal-button-primary ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="auth-loading-spinner"></div>
              ) : (
                getTranslation(t_nested, 'auth.logIn', 'Log In')
              )}
            </button>
          </form>
        )}

        <div className="auth-modal-footer">
          <span className="auth-modal-footer-text">
            {getTranslation(t_nested, 'auth.noAccount', "Don't have an account? ")}
          </span>
          <button
            type="button"
            className="auth-modal-footer-link"
            onClick={() => {
              onClose();
              onSwitchToSignup();
            }}
            disabled={isLoading}
          >
            {getTranslation(t_nested, 'auth.signUp', 'Sign up')}
          </button>
        </div>
      </div>
  );

  if (fullPage) {
    return content;
  }

  return (
    <AuthModal isOpen={isOpen} onClose={onClose} hideCloseButton>
      {content}
    </AuthModal>
  );
};

